import type { ReactNode } from 'react';
import { Header, type HeaderBrand, type HeaderMetaItem, type HeaderUser } from './Header';
import { Nav, type NavGroup } from './Nav';
import { Footer } from './Footer';
import { FreshnessBanner, type FreshnessBannerState } from './FreshnessBanner';
import { WatermarkOverlay } from './WatermarkOverlay';

/**
 * AppShell — editorial paper aesthetic layout frame.
 *
 * Pure presentational. Composes the topbar, optional freshness banner, the
 * left navigation, a main content slot, the editorial footer, and an optional
 * watermark overlay. No router (children replace <Outlet/>), no stores, no
 * auth, no data fetching.
 *
 * Structure:
 *   .app-root (flex column, full-height, paper bg)
 *     Header
 *     [FreshnessBanner]            when `freshness` is provided
 *     .app-body (flex row)
 *       Nav
 *       <main id="main-content" class="app-main">{children}</main>
 *     Footer
 *     [WatermarkOverlay]           when `watermark` is provided
 */

export interface AppShellFreshness {
  state: FreshnessBannerState;
  label: string;
}

export interface AppShellProps {
  brand: HeaderBrand;
  meta?: HeaderMetaItem[];
  user?: HeaderUser;
  freshness?: AppShellFreshness;
  navGroups: NavGroup[];
  activeId: string;
  onNavigate: (id: string) => void;
  watermark?: string;
  children: ReactNode;
}

export function AppShell({
  brand,
  meta,
  user,
  freshness,
  navGroups,
  activeId,
  onNavigate,
  watermark,
  children,
}: AppShellProps) {
  return (
    <div
      className="app-root"
      style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--paper)' }}
    >
      <Header brand={brand} meta={meta} user={user} />
      {freshness && <FreshnessBanner state={freshness.state} label={freshness.label} />}

      <div className="app-body" style={{ display: 'flex', flex: '1 1 auto', minHeight: 0 }}>
        <Nav groups={navGroups} activeId={activeId} onNavigate={onNavigate} />
        <main
          id="main-content"
          tabIndex={-1}
          className="app-main"
          style={{ flex: '1 1 auto', minWidth: 0, overflowX: 'auto', background: 'var(--paper)' }}
        >
          {children}
        </main>
      </div>

      <Footer left="Starter Kit · v1" right="Cache-Control: no-store" />
      {watermark && <WatermarkOverlay text={watermark} />}
    </div>
  );
}
